/**
 * User activity modal — timeline of messages, assets, projects by a user.
 * Entry points: chat message avatar, member list, search results.
 */
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useAppStore } from '@/store/appStore';
import { MessageSquare, Image, FolderOpen, X } from 'lucide-react';

type Props = {
  userId: Id<'users'>;
  open: boolean;
  onClose: () => void;
};

function formatTime(ts: number) {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString() : d.toLocaleString();
}

export function UserActivityModal({ userId, open, onClose }: Props) {
  const user = useQuery(api.users.getById, { id: userId });
  const activity = useQuery(api.userActivity.byUser, { userId });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[85vh] rounded-xl border border-border bg-card shadow-xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4 flex-shrink-0">
          <h3 className="font-semibold">
            {user ? (user.name || user.email || 'User') : '…'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activity === undefined ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : activity === null ? (
            <p className="text-sm text-muted-foreground">No access or no activity.</p>
          ) : (
            <>
              {activity.messages.length > 0 && (
                <section>
                  <h4 className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                    <MessageSquare className="h-3.5 w-3.5" />
                    Recent messages ({activity.messages.length})
                  </h4>
                  <ul className="space-y-2">
                    {activity.messages.slice(0, 15).map((m: any) => (
                      <li
                        key={m.id}
                        className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
                      >
                        <p className="line-clamp-2">{m.content}</p>
                        <span className="text-xs text-muted-foreground">
                          {formatTime(m.timestamp)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {activity.assets.length > 0 && (
                <section>
                  <h4 className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                    <Image className="h-3.5 w-3.5" />
                    Assets ({activity.assets.length})
                  </h4>
                  <ul className="space-y-1">
                    {activity.assets.slice(0, 15).map((a: any) => (
                      <li
                        key={a._id}
                        className="rounded px-2 py-1 text-sm bg-muted/30"
                      >
                        {a.name} <span className="text-muted-foreground">({a.type})</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {activity.projects.length > 0 && (
                <section>
                  <h4 className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                    <FolderOpen className="h-3.5 w-3.5" />
                    Projects edited ({activity.projects.length})
                  </h4>
                  <ul className="space-y-1">
                    {activity.projects.slice(0, 10).map((p: any) => (
                      <li
                        key={p._id}
                        className="rounded px-2 py-1 text-sm bg-muted/30"
                      >
                        {p.name}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {activity.messages.length === 0 &&
                activity.assets.length === 0 &&
                activity.projects.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent activity.</p>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
